// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { setSession, isLoggedIn } from './lib/auth';
import PrivateRoute from './components/PrivateRoute';
import MainApp from './components/MainApp';
import Login from './components/Login';
import Verify from './components/Verify';
import Register from './components/Register';
import Subscribe from './components/Subscribe';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Rewards from './components/Rewards';
import QRCodePage from './components/QRCodePage';
import Campaigns from './components/Campaigns';
import Notifications from './components/Notifications';
import Help from './components/Help';
import Collaborations from './components/Collaborations';
import StoreSettings from './components/StoreSettings';
import Credits from './components/Credits';
import LandingPage from './components/LandingPage';
import Contact from './components/Contact';
import Blogs from './components/Blogs';
import BlogPost from './components/BlogPost';
import LegalPage from './components/LegalPage';

import AdminPrivateRoute from './components/admin/AdminPrivateRoute';
import AdminLogin from './components/admin/AdminLogin';
import AdminVerify from './components/admin/AdminVerify';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminResources from './components/admin/AdminResources';
import AdminResourceEditor from './components/admin/AdminResourceEditor';
import AdminCustomers from './components/admin/AdminCustomers';
import AdminVendors from './components/admin/AdminVendors';
import AdminLandingPage from './components/admin/AdminLandingPage';
import AdminBusinessCategories from './components/admin/AdminBusinessCategories';
import AdminEmails from './components/admin/AdminEmails';
import AdminSettings from './components/admin/AdminSettings';
import AdminHelp from './components/admin/AdminHelp';
import AdminCredits from './components/admin/AdminCredits';
import AdminCollaborations from './components/admin/AdminCollaborations';
import AdminBlogs from './components/admin/AdminBlogs';
import AdminBlogEditor from './components/admin/AdminBlogEditor';


// Returning from Stripe Checkout, the URL carries a single-use ?rt= token.
// Redeem it into a real session BEFORE any route renders — PrivateRoute would
// otherwise bounce a vendor who just paid straight to the login page.
// Runs at the app shell so it covers every checkout return URL.
function useCheckoutResume() {
  const [resuming, setResuming] = useState(() =>
    new URLSearchParams(window.location.search).has('rt')
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('rt');
    if (!rt) return;

    let cancelled = false;

    const stripFromUrl = () => {
      params.delete('rt');
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      );
    };

    (async () => {
      // An existing valid session wins; just clean the URL.
      if (isLoggedIn()) {
        stripFromUrl();
        if (!cancelled) setResuming(false);
        return;
      }
      try {
        const res = await fetch('/api/auth/checkout-resume', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: rt }),
        });
        const data = await res.json();
        if (data.ok && data.token) setSession(data.token);
      } catch {
        // Fall through — the user lands on the login page, same as before.
      } finally {
        stripFromUrl();
        if (!cancelled) setResuming(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return resuming;
}

export default function App() {
  const resuming = useCheckoutResume();

  // Hold the router back until the session is restored, so PrivateRoute sees
  // the token rather than redirecting mid-flight.
  if (resuming) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontFamily: 'system-ui, sans-serif' }}>
        Finalising your payment…
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blogs" element={<Blogs />} />
      <Route path="/blogs/:slug" element={<BlogPost />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify/:token" element={<Verify />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/subscribe/:userId" element={<PrivateRoute><Subscribe /></PrivateRoute>} />

      <Route path="/dashboard/:userId" element={<PrivateRoute><MainApp/></PrivateRoute>}/>
      <Route path="/rewards/:userId" element={<PrivateRoute><Rewards/></PrivateRoute>}/>
      <Route path="/qr-code/:userId" element={<PrivateRoute><QRCodePage/></PrivateRoute>}/>
      <Route path="/campaigns/:userId" element={<PrivateRoute><Campaigns/></PrivateRoute>}/>
      <Route path="/notifications/:userId" element={<PrivateRoute><Notifications/></PrivateRoute>}/>
      <Route path="/collaborations/:userId" element={<PrivateRoute><Collaborations/></PrivateRoute>}/>
      <Route path="/credits/:userId" element={<PrivateRoute><Credits/></PrivateRoute>}/>
      <Route path="/help/:userId" element={<PrivateRoute><Help/></PrivateRoute>}/>
      <Route path="/store-settings/:userId" element={<PrivateRoute><StoreSettings/></PrivateRoute>}/>

      {/* Admin Portal */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/verify" element={<AdminVerify />} />
      <Route path="/admin" element={<AdminPrivateRoute><AdminLayout /></AdminPrivateRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="resources" element={<AdminResources />} />
        <Route path="resources/:id" element={<AdminResourceEditor />} />
        <Route path="landing-page" element={<AdminLandingPage />} />
        <Route path="business-categories" element={<AdminBusinessCategories />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="help" element={<AdminHelp />} />
        <Route path="credits" element={<AdminCredits />} />
        <Route path="collaborations" element={<AdminCollaborations />} />
        <Route path="blogs" element={<AdminBlogs />} />
        <Route path="blogs/new" element={<AdminBlogEditor />} />
        <Route path="blogs/:id" element={<AdminBlogEditor />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="vendors" element={<AdminVendors />} />
      </Route>

      <Route path="/*" element={<LandingPage />} />
    </Routes>
  );
}